// src/components/Game/Game.tsx
import React from 'react';
import { VStack, Box, HStack, IconButton, Tooltip, useColorModeValue } from '@chakra-ui/react';
import { FiFlag } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useGame } from '../../contexts/GameContext.tsx';
import { useSound } from '../../contexts/SoundContext.tsx';
import CategoryGrid from './CategoryGrid.tsx';
import QuestionCard from './QuestionCard.tsx';
import GameOver from './GameOver.tsx';
import HelpPopover from '../Tutorial/HelpPopover.tsx';
import { SoundControl } from '../Sound/SoundControl.tsx';

interface GameProps {
  onBackToLobby?: () => void;
}

const Game: React.FC<GameProps> = ({ onBackToLobby }) => {
  const { state, endGameManually } = useGame();
  const { playSound } = useSound();
  const { t } = useTranslation();

  const handleEndGame = () => {
    playSound('gameLose'); // Son de fin de partie
    endGameManually();
  };

  // On détermine quel composant afficher avec une logique plus explicite
  const renderContent = () => {
    if (state.isGameOver) {
      return <GameOver onBackToLobby={onBackToLobby} />;
    }
    if (state.currentQuestion) {
      return <QuestionCard />;
    }
    // Par défaut, on affiche la grille des catégories
    return <CategoryGrid />;
  };

  return (
    <>
      <VStack spacing={8} align="center" w="full" justify="center" minH="100vh">
        {/* Contrôles en haut - mobile friendly */}
        <Box 
          position="fixed" 
          top={{ base: 2, md: 4 }} 
          right={{ base: 2, md: 4 }} 
          left={{ base: 2, md: "auto" }}
          zIndex={1000}
          display="flex"
          justifyContent={{ base: "space-between", md: "flex-end" }}
          alignItems="center"
          maxW={{ base: "calc(100vw - 16px)", md: "auto" }}
          px={{ base: 2, md: 0 }}
        >
          <HStack spacing={{ base: 1, md: 2 }}>
            {/* Bouton terminer partie - seulement si le jeu n'est pas fini */}
            {!state.isGameOver && (
              <Tooltip label={t('game.endGameManually')} hasArrow>
                <IconButton
                  aria-label={t('game.endGameManually')}
                  icon={<FiFlag />}
                  size="sm"
                  variant="ghost"
                  color="white"
                  _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                  onClick={handleEndGame}
                />
              </Tooltip>
            )}
            <SoundControl 
              size="sm" 
              variant="compact" 
              iconColor="white" 
            />
            <HelpPopover />
          </HStack>
        </Box>
        
        {/* Contenu principal centré normalement */}
        {renderContent()}
      </VStack>
    </>
  );
};

export default Game;